import { Injectable, inject } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable, { Styles } from 'jspdf-autotable';
import { Borders, Cell, Workbook, Worksheet } from 'exceljs';
import { PVT_CONSTANTS, reportConstants, reportConstantsMessage } from '../../common/constants';
import { CVDReportData } from '../../models/lab-test';
import { CVDLastStageOilCompositionResult, CVDReport, CVDStageResult, CVDStageResultData, CVDVaporCompositions } from '../../cvd/models/cvd';
import { ReportGeneralUtilityService } from '../report.generalUtility.service';
import { setNumericOrTextCellValue } from './report.cellFormat.util';

@Injectable({
    providedIn: 'root'
})
export class ReportPDFCvdRenderService {
    private _reportGeneralUtilityService = inject(ReportGeneralUtilityService);

    private commonStyles = {
        fontSize: 8,
        textColor: [0, 0, 0],
        fillColor: false,
        lineColor: [0, 0, 0],
        lineWidth: reportConstants.tableLinewidth
    };

    private commonHeadStyles = {
        fontSize: 10,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        fontStyle: reportConstants.normal,
        font: reportConstants.eMprintCondensedRegular,
        cellPadding: { top: 0.5, bottom: 0.5 },
        lineWidth: 0
    };

    private commonBodyStylesOthers = {
        fontSize: 10,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        font: reportConstants.eMprintCondensedRegular,
        cellPadding: { top: 0.5, bottom: 0.5 }
    };

    private commonStylesWithoutBorder = {
        fontSize: 8,
        textColor: [0, 0, 0],
        fillColor: false,
        lineColor: [0, 0, 0],
        lineWidth: 0
    };

    private commonHeadStylesWithoutBorder = {
        fontSize: 10,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        fontStyle: reportConstants.normal,
        font: reportConstants.eMprintCondensedBold,
        cellPadding: { top: 0.5, bottom: 0.5 },
        lineWidth: 0
    };

    private commonBodyStylesWithoutBorder = {
        fontSize: 8,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        font: reportConstants.eMprintCondensedRegular,
        cellPadding: { top: 0.5, bottom: 0.5 },
        lineWidth: 0
    };

    CVDStage(doc: jsPDF, marginSize: number, cvdExp: CVDStageResult, appendix = '', wellNameVersion: string, sampleNameVersion: string): void {
        if (cvdExp !== undefined && cvdExp !== null) {
            doc.addPage();
            let tableStartY = marginSize;
            tableStartY += 20;
            doc.setFontSize(14);
            doc.setFont(reportConstants.eMprintCondensedBold);
            doc.text(appendix + reportConstants.constantVolumeDepletionAt + ' ' + cvdExp.reservoirTemperature + reportConstants.fTemp, marginSize - 7, tableStartY);
            tableStartY += 5;
            doc.setFontSize(10);
            doc.setFont(reportConstants.eMprintCondensedRegular);
            doc.text(wellNameVersion + ' ' + sampleNameVersion, marginSize - 7, tableStartY);
            tableStartY += 5;
            autoTable(doc, {
                head: reportConstants.cvdStageHead,
                body: cvdExp.cvdStageResultData.map((val: CVDStageResultData) => [
                    val.stage,
                    this.formatCvdStageValue(val.pressure, 0),
                    this.formatCvdStageValue(val.zFactorEquilibriumGas, 4),
                    this.formatCvdStageValue(val.zFactor2Phase, 4),
                    this.formatCvdStageValue(val.prodWellCumulativeMole, 1),
                    this.formatCvdStageValue(val.retrogradeLiquidVolume, 2),
                    this.formatCvdStageValue(val.gasPhaseDensity, 4)
                ]),
                startY: Math.round(tableStartY),
                theme: reportConstants.plain,
                margin: { left: marginSize - 7, right: marginSize - 7 },
                styles: this.commonStylesWithoutBorder as Partial<Styles>,
                headStyles: {
                    fontSize: 10,
                    halign: reportConstants.center,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    fontStyle: reportConstants.normal,
                    font: reportConstants.eMprintCondensedBold,
                    cellPadding: { top: 0.5, bottom: 0.5 },
                    lineWidth: 0
                },
                bodyStyles: this.commonBodyStylesOthers as Partial<Styles>,
                didDrawCell: function (data) {
                    const { cell, row, doc, section } = data;
                    if (section === reportConstants.head) {
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;

                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);

                        if (row.index === 0) {
                            doc.line(x, y, x + w, y);
                            doc.line(x, y, x, y + h);
                            doc.line(x + w, y, x + w, y + h);
                        } else if (row.index === 3) {
                            doc.line(x, y + h - 0.75, x + w, y + h - 0.75);
                            doc.line(x, y + h, x + w, y + h);
                            doc.line(x, y, x, y + h);
                            doc.line(x + w, y, x + w, y + h);
                        } else {
                            doc.line(x, y, x, y + h);
                            doc.line(x + w, y, x + w, y + h);
                        }
                    } else {
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.rect(cell.x, cell.y, cell.width, cell.height);
                    }
                }
            });
            tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;
            doc.setFontSize(10);
            tableStartY += 10;
            doc.setFont(reportConstants.eMprintCondensedRegular);
            doc.text(reportConstants.comments, marginSize, tableStartY);
            doc.text(reportConstants.cvdStageComments[0], marginSize, tableStartY + 5);
            doc.text(reportConstants.cvdStageComments[1], marginSize, tableStartY + 10);
            doc.text(reportConstants.cvdStageComments[2], marginSize, tableStartY + 15);
        }
    }

    CVDOtherPages(doc: jsPDF, marginSize: number, cvdExp: CVDReport, wellNameVersion: string, sampleNameVersion: string): void {
        let tableStartY = marginSize + 20;
        if (cvdExp?.vaporCompositionResult) {
            doc.addPage();
            doc.setFontSize(14);
            doc.setFont(reportConstants.eMprintCondensedBold);
            doc.text(reportConstants.constantVolumeDepletionVaporCompositions, marginSize - 7, tableStartY);
            tableStartY += 5;

            doc.setFontSize(10);
            doc.setFont(reportConstants.eMprintCondensedRegular);
            doc.text(`${wellNameVersion} ${sampleNameVersion}`, marginSize - 7, tableStartY);
            tableStartY += 5;

            const headerDataVapor = cvdExp.vaporCompositionResult.vaporPressure;
            const gridData = cvdExp.vaporCompositionResult.gridListData;

            const stageCount = cvdExp.cvdStageResult.cvdStageResultData.length - 1;
            const stageKeys = Array.from({ length: stageCount }, (_, i) => `stage${i + 1}`);

            const headRows: (string | number)[][] = [
                ['', ...stageKeys.map((_, i) => `Stage ${i + 1}`)],
                [reportConstants.pressurePsia, ...stageKeys.map(key => headerDataVapor[key as keyof typeof headerDataVapor] ?? '')],
                [reportConstants.component, ...stageKeys.map(() => reportConstants.molePercent)]
            ];

            const bodyRows = gridData.map((val: CVDVaporCompositions) => [
                val.pressure ?? '',
                ...stageKeys.map(key => this.formatCvdCompositionValue(val[key as keyof CVDVaporCompositions]))
            ]);

            autoTable(doc, {
                head: headRows,
                body: bodyRows,
                startY: Math.round(tableStartY),
                theme: reportConstants.plain,
                margin: { left: marginSize - 7, right: marginSize - 7 },
                styles: this.commonStylesWithoutBorder as Partial<Styles>,
                headStyles: this.commonHeadStylesWithoutBorder as Partial<Styles>,
                bodyStyles: this.commonBodyStylesWithoutBorder as Partial<Styles>,
                pageBreak: reportConstants.auto,
                didDrawCell: function (data) {

                    const { cell, row, column, doc, section } = data;
                    if (section === reportConstants.head && row.index === 0 && column.index === 0) {
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;

                        doc.setDrawColor(255, 255, 255);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.line(x, y, x + w, y);
                        doc.line(x, y + h, x + w, y + h);
                        doc.line(x, y, x, y + h);
                        doc.line(x + w, y, x + w, y + h);
                    }
                    else {
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.rect(cell.x, cell.y, cell.width, cell.height);
                    }

                    if (section === reportConstants.head && row.index === 2) {
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;
                        doc.line(x, y + h - 0.75, x + w, y + h - 0.75);
                        doc.line(x, y + h, x + w, y + h);
                        doc.line(x, y, x, y + h);
                        doc.line(x + w, y, x + w, y + h);
                    }
                }
            });
        }
        if (cvdExp?.lastStageOilCompositionResult) {
            doc.addPage();
            tableStartY = marginSize + 10;
            marginSize += 5;
            doc.setFont(reportConstants.eMprintCondensedBold);
            doc.text(reportConstants.constantVolumeDepletionLastStageOil, marginSize + 20, tableStartY);
            tableStartY += 5;
            autoTable(doc, {
                head: [
                    [
                        { content: '', colSpan: 2, styles: { halign: reportConstants.center } },
                        { content: ' ' + reportConstants.lastStageOil, colSpan: 2, styles: { halign: reportConstants.center } }
                    ],
                    [
                        { content: reportConstants.component, colSpan: 2, styles: { halign: reportConstants.center } },
                        { content: reportConstants.weightPercent, styles: { halign: reportConstants.center } },
                        { content: reportConstants.molePercent, styles: { halign: reportConstants.center } }
                    ]
                ],
                body: cvdExp.lastStageOilCompositionResult.map((val: CVDLastStageOilCompositionResult) => [
                    val.component1,
                    val.component2,
                    this.formatCvdCompositionValue(val.weightPercent),
                    this.formatCvdCompositionValue(val.molePercent)
                ]),
                startY: Math.round(tableStartY),
                theme: reportConstants.plain,
                margin: { left: marginSize + 20, right: marginSize + 20 },
                styles: this.commonStylesWithoutBorder as Partial<Styles>,
                headStyles: this.commonHeadStylesWithoutBorder as Partial<Styles>,
                bodyStyles: this.commonBodyStylesWithoutBorder as Partial<Styles>,
                didParseCell: function (data) {
                    if (data.section === reportConstants.body && (data.column.index === 0 || data.column.index === 1)) {
                        data.cell.styles.font = reportConstants.eMprintCondensedBold;
                    }
                },
                didDrawCell: function (data) {
                    const { cell, row, column, doc, section } = data;
                    if (section === reportConstants.head && row.index === 0 && column.index === 0) {
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;

                        doc.setDrawColor(255, 255, 255);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.line(x, y, x + w, y);
                        doc.line(x, y + h, x + w, y + h);
                        doc.line(x, y, x, y + h);
                        doc.line(x + w, y, x + w, y + h);
                    }
                    else {
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        doc.rect(cell.x, cell.y, cell.width, cell.height);
                    }
                    if (section === reportConstants.head && row.index === 1) {
                        doc.setDrawColor(0, 0, 0);
                        doc.setLineWidth(reportConstants.tableLinewidth);
                        const x = cell.x;
                        const y = cell.y;
                        const w = cell.width;
                        const h = cell.height;
                        doc.line(x, y + h - 0.75, x + w, y + h - 0.75);
                        doc.line(x, y + h, x + w, y + h);
                        doc.line(x, y, x, y + h);
                        doc.line(x + w, y, x + w, y + h);
                    }
                }
            });
            tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;
        }
        if (cvdExp?.materialBalanceSummaryResult) {
            doc.addPage();
            tableStartY = marginSize + 20;
            doc.text(reportConstants.materialBalanceCalculationSummary, marginSize, tableStartY);
            tableStartY += 10;
            doc.text(reportConstants.molesOfReservoirFluidCVD, marginSize, tableStartY + 3);
            const massesReservoirFluid = cvdExp.materialBalanceSummaryResult.molesReservoirFluid;
            const constTableData = [
                [reportConstants.initialMoles, this.formatCvdMaterialBalanceValue(massesReservoirFluid[0].initialMoles, 4), massesReservoirFluid[1].initialMoles],
                [reportConstants.materialBalanceCalculatedMoles, this.formatCvdMaterialBalanceValue(massesReservoirFluid[0].calculatedMoles, 4), massesReservoirFluid[1].calculatedMoles],
                [reportConstants.dlDifference, this.formatCvdMaterialBalanceValue(massesReservoirFluid[0].difference, 2), massesReservoirFluid[1].difference]
            ];

            autoTable(doc, {
                head: [['', '', '']],
                body: constTableData,
                startY: tableStartY,
                theme: reportConstants.grid,
                margin: { left: marginSize, right: marginSize },
                styles: this.commonStyles as Partial<Styles>,
                headStyles: this.commonHeadStyles as Partial<Styles>,
                bodyStyles: {
                    fontSize: 12,
                    halign: reportConstants.center,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    font: reportConstants.eMprintCondensedRegular,
                    cellPadding: { top: 0.5, bottom: 0.5 },
                    lineWidth: reportConstants.tableLinewidth
                },
                columnStyles: {
                    0: { cellWidth: 100 },
                    1: { cellWidth: 20 },
                    2: { cellWidth: 20 }
                },
                didParseCell: function (data) {
                    if (data.section === reportConstants.body && data.column.index === 0) {
                        data.cell.styles.halign = reportConstants.left;
                        data.cell.styles.cellPadding = { left: 2, right: 0, top: 0.5, bottom: 0.5 };
                    }
                    if (data.section === reportConstants.body && data.column.index === 2) {
                        data.cell.styles.halign = reportConstants.left;
                    }
                    data.cell.styles.cellPadding = { left: 2, right: 2, top: 0.5, bottom: 0.5 };
                }
            });

            tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;
            doc.text(reportConstants.molecularWeightOfReservoirFluid, marginSize, tableStartY + 3);
            const massesStockTankOil = cvdExp.materialBalanceSummaryResult.reservoirFluidMW;
            const msstTableData = [
                [reportConstants.reservoirFluidMW, this.formatCvdMaterialBalanceValue(massesStockTankOil[0].reservoirFluidMW, 2), massesStockTankOil[1].reservoirFluidMW],
                [reportConstants.materialBalanceCalculatedReservoirFluidMW, this.formatCvdMaterialBalanceValue(massesStockTankOil[0].calculatedResFluidMW, 2), massesStockTankOil[1].calculatedResFluidMW],
                [reportConstants.dlDifference, this.formatCvdMaterialBalanceValue(massesStockTankOil[0].difference, 2), massesStockTankOil[1].difference]
            ];
            autoTable(doc, {
                head: [['', '', '']],
                body: msstTableData,
                startY: tableStartY,
                theme: reportConstants.grid,
                margin: { left: marginSize, right: marginSize },
                styles: this.commonStyles as Partial<Styles>,
                headStyles: this.commonHeadStyles as Partial<Styles>,
                bodyStyles: {
                    fontSize: 12,
                    halign: reportConstants.center,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    font: reportConstants.eMprintCondensedRegular,
                    cellPadding: { top: 0.5, bottom: 0.5 },
                    lineWidth: reportConstants.tableLinewidth
                },
                columnStyles: {
                    0: { cellWidth: 100 },
                    1: { cellWidth: 20 },
                    2: { cellWidth: 20 }
                },
                didParseCell: function (data) {
                    if (data.section === reportConstants.body && data.column.index === 0) {
                        data.cell.styles.halign = reportConstants.left;
                        data.cell.styles.cellPadding = { left: 2, right: 0, top: 0.5, bottom: 0.5 };
                    }
                    if (data.section === reportConstants.body && data.column.index === 2) {
                        data.cell.styles.halign = reportConstants.left;
                    }
                    data.cell.styles.cellPadding = { left: 2, right: 2, top: 0.5, bottom: 0.5 };
                }
            });

            tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;
            doc.text(reportConstants.molesOfLastStageLiveOil, marginSize, tableStartY + 3);
            const molesLastStageLiveOil = cvdExp.materialBalanceSummaryResult.molesLastStageLiveOil;
            const cvdMolesTableData = [
                [reportConstants.measuredOilMoles, this.formatCvdMaterialBalanceValue(molesLastStageLiveOil[0].measuredOilMoles, 4), molesLastStageLiveOil[1].measuredOilMoles],
                [reportConstants.materialBalanceCalculatedOilMoles, this.formatCvdMaterialBalanceValue(molesLastStageLiveOil[0].calculatedOilMoles, 4), molesLastStageLiveOil[1].calculatedOilMoles],
                [reportConstants.dlDifference, this.formatCvdMaterialBalanceValue(molesLastStageLiveOil[0].difference, 2), molesLastStageLiveOil[1].difference]
            ];
            autoTable(doc, {
                head: [['', '', '']],
                body: cvdMolesTableData,
                startY: tableStartY,
                theme: reportConstants.grid,
                margin: { left: marginSize, right: marginSize },
                styles: this.commonStyles as Partial<Styles>,
                headStyles: this.commonHeadStyles as Partial<Styles>,
                bodyStyles: {
                    fontSize: 12,
                    halign: reportConstants.center,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    font: reportConstants.eMprintCondensedRegular,
                    cellPadding: { top: 0.5, bottom: 0.5 },
                    lineWidth: reportConstants.tableLinewidth
                },
                columnStyles: {
                    0: { cellWidth: 100 },
                    1: { cellWidth: 20 },
                    2: { cellWidth: 20 }
                },
                didParseCell: function (data) {
                    if (data.section === reportConstants.body && data.column.index === 0) {
                        data.cell.styles.halign = reportConstants.left;
                        data.cell.styles.cellPadding = { left: 2, right: 0, top: 0.5, bottom: 0.5 };
                    }
                    if (data.section === reportConstants.body && data.column.index === 2) {
                        data.cell.styles.halign = reportConstants.left;
                    }
                    data.cell.styles.cellPadding = { left: 2, right: 2, top: 0.5, bottom: 0.5 };
                }
            });

            tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;
            doc.text(reportConstants.molecularWeightOfLastStageOil, marginSize, tableStartY + 3);
            const mwLastStageOil = cvdExp.materialBalanceSummaryResult.molecularWeightLastStageOil;
            const cvdMWTableData = [
                [reportConstants.measuredOilMW, this.formatCvdMaterialBalanceValue(mwLastStageOil[0].measuredOilMW, 2), mwLastStageOil[1].measuredOilMW],
                [reportConstants.materialBalanceCalculatedOilMW, this.formatCvdMaterialBalanceValue(mwLastStageOil[0].calculatedOilMW, 2), mwLastStageOil[1].calculatedOilMW],
                [reportConstants.dlDifference, this.formatCvdMaterialBalanceValue(mwLastStageOil[0].difference, 2), mwLastStageOil[1].difference]
            ];
            autoTable(doc, {
                head: [['', '', '']],
                body: cvdMWTableData,
                startY: tableStartY,
                theme: reportConstants.grid,
                margin: { left: marginSize, right: marginSize },
                styles: this.commonStyles as Partial<Styles>,
                headStyles: this.commonHeadStyles as Partial<Styles>,
                bodyStyles: {
                    fontSize: 12,
                    halign: reportConstants.center,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    font: reportConstants.eMprintCondensedRegular,
                    cellPadding: { top: 0.5, bottom: 0.5 },
                    lineWidth: reportConstants.tableLinewidth
                },
                columnStyles: {
                    0: { cellWidth: 100 },
                    1: { cellWidth: 20 },
                    2: { cellWidth: 20 }
                },
                didParseCell: function (data) {
                    if (data.section === reportConstants.body && data.column.index === 0) {
                        data.cell.styles.halign = reportConstants.left;
                        data.cell.styles.cellPadding = { left: 2, right: 0, top: 0.5, bottom: 0.5 };
                    }
                    if (data.section === reportConstants.body && data.column.index === 2) {
                        data.cell.styles.halign = reportConstants.left;
                    }
                    data.cell.styles.cellPadding = { left: 2, right: 2, top: 0.5, bottom: 0.5 };
                }
            });

            tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;

            doc.text(reportConstants.weightBalance, marginSize, tableStartY + 3);
            const molesReservoirFluidWeight = cvdExp.materialBalanceSummaryResult.weightBalance;
            const molesTableData = [
                [reportConstants.gasWeightLoaded, this.formatCvdMaterialBalanceValue(molesReservoirFluidWeight[0].gasWeightLoaded, 2), molesReservoirFluidWeight[1].gasWeightLoaded],
                [reportConstants.weightCollected, this.formatCvdMaterialBalanceValue(molesReservoirFluidWeight[0].weightCollected, 2), molesReservoirFluidWeight[1].weightCollected],
                [reportConstants.dlDifference, this.formatCvdMaterialBalanceValue(molesReservoirFluidWeight[0].difference, 2), molesReservoirFluidWeight[1].difference]
            ];
            autoTable(doc, {
                head: [['', '', '']],
                body: molesTableData,
                startY: tableStartY,
                theme: reportConstants.grid,
                margin: { left: marginSize, right: marginSize },
                styles: this.commonStyles as Partial<Styles>,
                headStyles: this.commonHeadStyles as Partial<Styles>,
                bodyStyles: {
                    fontSize: 12,
                    halign: reportConstants.center,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    font: reportConstants.eMprintCondensedRegular,
                    cellPadding: { top: 0.5, bottom: 0.5 },
                    lineWidth: reportConstants.tableLinewidth
                },
                columnStyles: {
                    0: { cellWidth: 100 },
                    1: { cellWidth: 20 },
                    2: { cellWidth: 20 }
                },
                didParseCell: function (data) {
                    if (data.section === reportConstants.body && data.column.index === 0) {
                        data.cell.styles.halign = reportConstants.left;
                        data.cell.styles.cellPadding = { left: 2, right: 0, top: 0.5, bottom: 0.5 };
                    }
                    if (data.section === reportConstants.body && data.column.index === 2) {
                        data.cell.styles.halign = reportConstants.left;
                    }
                    data.cell.styles.cellPadding = { left: 2, right: 2, top: 0.5, bottom: 0.5 };
                }
            });
        }
    }

    async GenerateCVD(workbook: Workbook, worksheetName: string, cvdExp: CVDReportData, wellName: string, sampleName: string, subHeader: string, appendix = '') {
        try {
            const cvdWorksheet = workbook.addWorksheet(worksheetName);

            const borderStyle = {
                top: { style: reportConstants.medium },
                left: { style: reportConstants.medium },
                bottom: { style: reportConstants.medium },
                right: { style: reportConstants.medium }
            };

            cvdWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(cvdWorksheet, undefined, {
                paperSize: undefined, // letter
                orientation: reportConstants.landscape, //we can set it to 'portrait' or 'landscape'
                fitToPage: false,
                fitToWidth: 0, //page 1
                fitToHeight: 0, // automatic
                scale: 100,
            });
            cvdWorksheet.getColumn(reportConstants.b).width = 5;

            const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });

            cvdWorksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            this._reportGeneralUtilityService.AddSubHeaderToWorksheet(cvdWorksheet, subHeader, reportConstants.c1, reportConstants.i1);
            cvdWorksheet.mergeCells(reportConstants.a4, reportConstants.h5);
            const textReporttitleCell = cvdWorksheet.getCell(reportConstants.a4);
            textReporttitleCell.value = appendix + reportConstants.constantVolumeDepletionAt + ' ' + cvdExp.cvdData.cvdStageResult.reservoirTemperature + reportConstants.fTemp;
            textReporttitleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            textReporttitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            
            cvdWorksheet.getRow(6).height = 20;
            cvdWorksheet.mergeCells(reportConstants.a6, reportConstants.h6);
            const textReportSubtitleCell = cvdWorksheet.getCell(reportConstants.a6);
            textReportSubtitleCell.value = wellName.replace(reportConstants.dstSample, '') + ' ' + sampleName;
            textReportSubtitleCell.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
            textReportSubtitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            let rowNo = 7;
            const sampleHead = reportConstants.sampleHeadCVD;
            const sampleBody = cvdExp.cvdData.cvdStageResult.cvdStageResultData.map((val: CVDStageResultData) => [
                val.stage,
                this.formatCvdStageValue(val.pressure, 0),
                this.formatCvdStageValue(val.zFactorEquilibriumGas, 4),
                this.formatCvdStageValue(val.zFactor2Phase, 4),
                val.stage === PVT_CONSTANTS.PSAT ? reportConstants.dash : this.formatCvdStageValue(val.prodWellCumulativeMole, 1),
                val.stage === PVT_CONSTANTS.PSAT ? reportConstants.dash : this.formatCvdStageValue(val.retrogradeLiquidVolume, 2),
                this.formatCvdStageValue(val.gasPhaseDensity, 4),
            ]);

            const tableStartRow = rowNo++;
            this.GenerateTableWithBorders(cvdWorksheet, sampleHead, sampleBody, tableStartRow, 2, borderStyle);
            const pressureColumnWidth = cvdWorksheet.getColumn(3).width;
            for (let column = 4; column <= 8; column++) {
                cvdWorksheet.getColumn(column).width = pressureColumnWidth! + 4;
            }
            rowNo = tableStartRow + sampleHead.length + sampleBody.length;

            rowNo += 1;
            this.AddComment(cvdWorksheet, reportConstants.b, rowNo, reportConstants.comments);
            rowNo++;
            this.AddComment(cvdWorksheet, reportConstants.b, rowNo, reportConstants.cvdComments1);
            rowNo++;
            this.AddComment(cvdWorksheet, reportConstants.b, rowNo, reportConstants.cvdComments2);
            rowNo++;
            this.AddComment(cvdWorksheet, reportConstants.b, rowNo, reportConstants.cvdComments3);
            rowNo++;

            rowNo += 2;
            this.AddOuterBorder(cvdWorksheet, 4, rowNo, 1, 9);
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }
    }

    private formatCvdStageValue(value: number | string | null | undefined, decimalPlaces: number): string {
        if (value === null || value === undefined) return '';
        const numericValue = Number(value);
        return Number.isNaN(numericValue) ? String(value) : numericValue.toFixed(decimalPlaces);
    }

    private formatCvdCompositionValue(value: number | string | null | undefined): string {
        if (value === null || value === undefined || value === '') return '';
        const numericValue = Number(value);
        return Number.isNaN(numericValue) ? String(value) : numericValue.toFixed(3);
    }

    private formatCvdMaterialBalanceValue(value: number | string | null | undefined, decimalPlaces: number): string {
        if (value === null || value === undefined || value === '') return '';
        const numericValue = Number(value);
        return Number.isNaN(numericValue) ? String(value) : numericValue.toFixed(decimalPlaces);
    }

    async GenerateCVDVapor(workbook: Workbook, worksheetName: string, cvdExp: CVDReportData, subHeader: string) {
        try {
            const cvdWorksheet = workbook.addWorksheet(worksheetName);

            const borderStyle = {
                top: { style: reportConstants.thin },
                left: { style: reportConstants.thin },
                bottom: { style: reportConstants.thin },
                right: { style: reportConstants.thin }
            };

            cvdWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(cvdWorksheet, undefined, {
                paperSize: undefined, // letter
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: true,
                fitToWidth: 1, //page 1
                fitToHeight: 1, // automatic
                scale: 65,
                horizontalCentered: true,
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                }
            });
            cvdWorksheet.getColumn(reportConstants.b).width = 5;

            const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });

            cvdWorksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            this._reportGeneralUtilityService.AddSubHeaderToWorksheet(cvdWorksheet, subHeader, reportConstants.c1, reportConstants.j1);

            const stagesData = cvdExp.cvdData.cvdStageResult.cvdStageResultData.filter((s: CVDStageResultData) => s.stage !== reportConstants.p_Sat);
            const stageCount = stagesData.length;
            const vaporData = cvdExp.cvdData.vaporCompositionResult?.gridListData || [];
            if (vaporData.length === 0) return;

            const lastCol = stageCount + 2;

            cvdWorksheet.mergeCells(reportConstants.b4, reportConstants.i4);
            cvdWorksheet.getRow(4).height = 20;
            const titleCell = cvdWorksheet.getCell(reportConstants.b4);
            titleCell.value = reportConstants.constantVolumeDepletionVaporCompositions;
            titleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            titleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle, wrapText: true };

            cvdWorksheet.getCell(6, 2).value = '';
            stagesData.forEach((stage: CVDStageResultData, i: number) => {
                const colIndex = i + 3;
                cvdWorksheet.getColumn(colIndex).width = 5;
                cvdWorksheet.getCell(6, colIndex).value = `Stage ${i + 1}`;
                cvdWorksheet.getCell(6, colIndex).font = { name: reportConstants.eMprintCondensed, size: 11, bold: true };
                cvdWorksheet.getCell(6, colIndex).alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            });

            cvdWorksheet.getCell(7, 2).value = reportConstants.pressurePsia;
            cvdWorksheet.getCell(7, 2).font = { name: reportConstants.eMprintCondensed, size: 11, bold: true };
            cvdWorksheet.getCell(7, 2).alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };

            stagesData.forEach((stage: CVDStageResultData, i: number) => {
                const colIndex = i + 3;
                setNumericOrTextCellValue(cvdWorksheet.getCell(7, colIndex), stage.pressure ?? '');
                cvdWorksheet.getCell(7, colIndex).font = { name: reportConstants.eMprintCondensed, size: 11, bold: true };
                cvdWorksheet.getCell(7, colIndex).alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            });

            cvdWorksheet.getCell(8, 2).value = reportConstants.component;
            cvdWorksheet.getCell(8, 2).font = { name: reportConstants.eMprintCondensed, size: 11, bold: true };
            cvdWorksheet.getCell(8, 2).alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };

            stagesData.forEach((stage: CVDStageResultData, i: number) => {
                const colIndex = i + 3;
                cvdWorksheet.getCell(8, colIndex).value = reportConstants.molePercent;
                cvdWorksheet.getCell(8, colIndex).font = { name: reportConstants.eMprintCondensed, size: 11, bold: true };
                cvdWorksheet.getCell(8, colIndex).alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            });

            const dataRowStart = 9;
            vaporData.forEach((comp: CVDVaporCompositions, idx: number) => {
                const rowIndex = dataRowStart + idx;
                setNumericOrTextCellValue(cvdWorksheet.getCell(rowIndex, 2), comp.pressure ?? '');
                cvdWorksheet.getCell(rowIndex, 2).font = { name: reportConstants.eMprintCondensed, size: 11, bold: true };
                cvdWorksheet.getCell(rowIndex, 2).alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };

                stagesData.forEach((stage: CVDStageResultData, i: number) => {
                    const colIndex = i + 3;
                    const value = comp[`stage${i + 1}`];
                    const cell = cvdWorksheet.getCell(rowIndex, colIndex);
                    setNumericOrTextCellValue(cell, this.formatCvdCompositionValue(value));
                    cell.font = { name: reportConstants.eMprintCondensed, size: 11 };
                    cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                });
            });

            const lastRow = dataRowStart + vaporData.length - 1;

            cvdWorksheet.getRow(5).eachCell((cell: Cell) => {
                cell.border = { left: { style: reportConstants.thin }, right: { style: reportConstants.thin } };
            });

            cvdWorksheet.getRow(6).eachCell((cell: Cell) => {
                cell.border = borderStyle as Partial<Borders>;
            });

            cvdWorksheet.getRow(7).eachCell((cell: Cell) => {
                cell.border = borderStyle as Partial<Borders>;
            });

            cvdWorksheet.getRow(8).eachCell((cell: Cell) => {
                cell.border = { top: { style: reportConstants.thin }, left: { style: reportConstants.thin }, bottom: { style: reportConstants.double }, right: { style: reportConstants.thin } };
            });

            for (let r = 9; r <= lastRow; r++) {
                for (let c = 2; c <= lastCol; c++) {
                    cvdWorksheet.getCell(r, c).border = borderStyle as Partial<Borders>;
                }
            }

            for (let r = 4; r <= 5; r++) {
                for (let c = 2; c <= lastCol; c++) {
                    cvdWorksheet.getCell(r, c).border = {};
                }
            }

            cvdWorksheet.getCell(6, 2).border = {
                bottom: { style: reportConstants.thin },
                right: { style: reportConstants.thin }
            };

            cvdWorksheet.getColumn(2).width = 15;
            for (let i = 3; i <= lastCol; i++) {
                cvdWorksheet.getColumn(i).width = 10;
            }
        } catch (error) {
            console.error(reportConstantsMessage.errorGeneratingVaporSheet, error);
        }
    }

    async GenerateCVDStageOil(workbook: Workbook, worksheetName: string, cvdExp: CVDReportData, subHeader: string) {
        try {
            const cvdWorksheet = workbook.addWorksheet(worksheetName);

            const borderStyle = {
                top: { style: reportConstants.thin },
                left: { style: reportConstants.thin },
                bottom: { style: reportConstants.thin },
                right: { style: reportConstants.thin }
            };

            const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });
            cvdWorksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            this._reportGeneralUtilityService.AddSubHeaderToWorksheet(cvdWorksheet, subHeader, reportConstants.c1, reportConstants.h1);

            cvdWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(cvdWorksheet, undefined, {
                paperSize: undefined, // letter
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: true,
                fitToWidth: 1, //page 1
                fitToHeight: 1, // automatic
                horizontalCentered: true,
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                },
                scale: 68
            });

            const oilData = cvdExp.cvdData.lastStageOilCompositionResult || [];
            if (oilData.length === 0) return;

            cvdWorksheet.mergeCells(4, 2, 5, 8);
            const titleCell = cvdWorksheet.getCell(reportConstants.b5);
            titleCell.value = reportConstants.constantVolumeDepletionLastStageOil;
            titleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            titleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle, wrapText: true };

            cvdWorksheet.mergeCells(6, 4, 6, 5);
            cvdWorksheet.getCell(6, 4).value = reportConstants.lastStageOil;
            cvdWorksheet.getCell(6, 4).font = { name: reportConstants.eMprintCondensed, size: 11, bold: true };
            cvdWorksheet.getCell(6, 4).alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };

            cvdWorksheet.mergeCells(7, 2, 7, 3);
            cvdWorksheet.getCell(7, 2).value = reportConstants.component;
            cvdWorksheet.getCell(7, 4).value = reportConstants.weight;
            cvdWorksheet.getCell(7, 5).value = reportConstants.molePercent;

            [2, 4, 5].forEach(col => {
                cvdWorksheet.getCell(7, col).font = { name: reportConstants.eMprintCondensed, size: 11, bold: true };
                cvdWorksheet.getCell(7, col).alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            });

            const dataRowStart = 8;
            oilData.forEach((comp: CVDLastStageOilCompositionResult, idx: number) => {
                const rowIndex = dataRowStart + idx;
                cvdWorksheet.getCell(rowIndex, 2).value = comp.component1;
                cvdWorksheet.getCell(rowIndex, 3).value = comp.component2;
                setNumericOrTextCellValue(cvdWorksheet.getCell(rowIndex, 4), this.formatCvdCompositionValue(comp.weightPercent));
                setNumericOrTextCellValue(cvdWorksheet.getCell(rowIndex, 5), this.formatCvdCompositionValue(comp.molePercent));

                [2, 3].forEach(col => {
                    cvdWorksheet.getCell(rowIndex, col).font = { name: reportConstants.eMprintCondensed, size: 11, bold: true };
                });
                [4, 5].forEach(col => {
                    cvdWorksheet.getCell(rowIndex, col).font = { name: reportConstants.eMprintCondensed, size: 11 };
                });

                [2, 3, 4, 5].forEach(col => {
                    cvdWorksheet.getCell(rowIndex, col).alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                });
            });

            const lastRow = dataRowStart + oilData.length - 1;

            cvdWorksheet.getRow(6).eachCell((cell: Cell) => (cell.border = borderStyle as Partial<Borders>));
            cvdWorksheet.getRow(7).eachCell((cell: Cell) => (cell.border = borderStyle as Partial<Borders>));
            for (let r = dataRowStart; r <= lastRow; r++) {
                for (let c = 2; c <= 5; c++) {
                    cvdWorksheet.getCell(r, c).border = borderStyle as Partial<Borders>;
                }
            }

            for (let r = 4; r <= 5; r++) {
                for (let c = 2; c <= 5; c++) {
                    cvdWorksheet.getCell(r, c).border = {};
                }
            }

            cvdWorksheet.getCell(6, 3).border = {
                bottom: { style: reportConstants.thin },
                right: { style: reportConstants.thin }
            };

            cvdWorksheet.getColumn(2).width = 14;
            cvdWorksheet.getColumn(3).width = 24;
            cvdWorksheet.getColumn(4).width = 10;
            cvdWorksheet.getColumn(5).width = 10;
        } catch (error) {
            console.error(reportConstantsMessage.errorGeneratingLastStageOilSheet, error);
        }
    }

    async GenerateCVDMaterialBalance(workbook: Workbook, worksheetName: string, cvdExp: CVDReportData, subHeader: string) {
        try {
            const cvdWorksheet = workbook.addWorksheet(worksheetName);

            const borderStyle = {
                top: { style: reportConstants.thin },
                left: { style: reportConstants.thin },
                bottom: { style: reportConstants.thin },
                right: { style: reportConstants.thin }
            };

            const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });

            cvdWorksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            this._reportGeneralUtilityService.AddSubHeaderToWorksheet(cvdWorksheet, subHeader, reportConstants.b1, reportConstants.e1);

            cvdWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(cvdWorksheet, undefined, {
                paperSize: undefined, // letter
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: false,
                fitToWidth: 0, //page 1
                fitToHeight: 0, // automatic 0
                scale: 100,
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                }
            });

            const mbData = cvdExp.cvdData.materialBalanceSummaryResult || {};
            if (!mbData) return;

            const molesData = mbData.molesReservoirFluid || [];
            const mwData = mbData.reservoirFluidMW || [];
            const molesLSLOData = mbData.molesLastStageLiveOil || [];
            const mwLSLOData = mbData.molecularWeightLastStageOil || [];
            const weightData = mbData.weightBalance || [];

            cvdWorksheet.mergeCells(reportConstants.a3, reportConstants.e3);
            const titleCell = cvdWorksheet.getCell(reportConstants.a3);
            titleCell.value = reportConstants.materialBalanceCalculationSummary;
            titleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            titleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle, wrapText: true };
            cvdWorksheet.getRow(3).height = 25;
            cvdWorksheet.getColumn(2).width = 56;
            cvdWorksheet.getColumn(3).width = 10;
            cvdWorksheet.getColumn(4).width = 8;

            let row = 5;
            const spacerRows: number[] = [];

            cvdWorksheet.mergeCells(row, 2, row, 4);
            cvdWorksheet.getCell(row, 2).value = reportConstants.molesOfReservoirFluidCVD;
            cvdWorksheet.getCell(row, 2).font = { bold: true, size: 12 };
            row++;

            cvdWorksheet.getCell(row, 2).value = reportConstants.initialMolesCVD;
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 3), this.formatCvdMaterialBalanceValue(molesData[0]?.initialMoles, 4));
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 4), molesData[1]?.initialMoles || '');
            row++;

            cvdWorksheet.getCell(row, 2).value = reportConstants.materialBalanceCalculatedMoles;
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 3), this.formatCvdMaterialBalanceValue(molesData[0]?.calculatedMoles, 4));
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 4), molesData[1]?.calculatedMoles || '');
            row++;

            cvdWorksheet.getCell(row, 2).value = reportConstants.dlDifference;
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 3), this.formatCvdMaterialBalanceValue(molesData[0]?.difference, 2));
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 4), molesData[1]?.difference || '');
            row++;

            spacerRows.push(row);
            [2, 3, 4].forEach(col => cvdWorksheet.getCell(row, col).value = '');
            row++;

            cvdWorksheet.mergeCells(row, 2, row, 4);
            cvdWorksheet.getCell(row, 2).value = reportConstants.molecularWeightOfReservoirFluid;
            cvdWorksheet.getCell(row, 2).font = { bold: true, size: 12 };
            row++;

            cvdWorksheet.getCell(row, 2).value = reportConstants.reservoirFluidMW;
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 3), this.formatCvdMaterialBalanceValue(mwData[0]?.reservoirFluidMW, 2));
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 4), mwData[1]?.reservoirFluidMW || '');
            row++;

            cvdWorksheet.getCell(row, 2).value = reportConstants.materialBalanceCalculatedReservoirFluidMW;
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 3), this.formatCvdMaterialBalanceValue(mwData[0]?.calculatedResFluidMW, 2));
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 4), mwData[1]?.calculatedResFluidMW || '');
            row++;

            cvdWorksheet.getCell(row, 2).value = reportConstants.dlDifference;
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 3), this.formatCvdMaterialBalanceValue(mwData[0]?.difference, 2));
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 4), mwData[1]?.difference || '');
            row++;

            spacerRows.push(row);
            [2, 3, 4].forEach(col => cvdWorksheet.getCell(row, col).value = '');
            row++;

            cvdWorksheet.mergeCells(row, 2, row, 4);
            cvdWorksheet.getCell(row, 2).value = reportConstants.molesOfLastStageLiveOil;
            cvdWorksheet.getCell(row, 2).font = { bold: true, size: 12 };
            row++;

            cvdWorksheet.getCell(row, 2).value = reportConstants.measuredOilMoles;
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 3), this.formatCvdMaterialBalanceValue(molesLSLOData[0]?.measuredOilMoles, 4));
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 4), molesLSLOData[1]?.measuredOilMoles || '');
            row++;

            cvdWorksheet.getCell(row, 2).value = reportConstants.materialBalanceCalculatedOilMoles;
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 3), this.formatCvdMaterialBalanceValue(molesLSLOData[0]?.calculatedOilMoles, 4));
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 4), molesLSLOData[1]?.calculatedOilMoles || '');
            row++;

            cvdWorksheet.getCell(row, 2).value = reportConstants.dlDifference;
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 3), this.formatCvdMaterialBalanceValue(molesLSLOData[0]?.difference, 2));
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 4), molesLSLOData[1]?.difference || '');
            row++;

            spacerRows.push(row);
            [2, 3, 4].forEach(col => cvdWorksheet.getCell(row, col).value = '');
            row++;

            cvdWorksheet.mergeCells(row, 2, row, 4);
            cvdWorksheet.getCell(row, 2).value = reportConstants.molecularWeightOfLastStageOil;
            cvdWorksheet.getCell(row, 2).font = { bold: true, size: 12 };
            row++;

            cvdWorksheet.getCell(row, 2).value = reportConstants.measuredOilMW;
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 3), this.formatCvdMaterialBalanceValue(mwLSLOData[0]?.measuredOilMW, 2));
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 4), mwLSLOData[1]?.measuredOilMW || '');
            row++;

            cvdWorksheet.getCell(row, 2).value = reportConstants.materialBalanceCalculatedOilMW;
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 3), this.formatCvdMaterialBalanceValue(mwLSLOData[0]?.calculatedOilMW, 2));
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 4), mwLSLOData[1]?.calculatedOilMW || '');
            row++;

            cvdWorksheet.getCell(row, 2).value = reportConstants.dlDifference;
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 3), this.formatCvdMaterialBalanceValue(mwLSLOData[0]?.difference, 2));
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 4), mwLSLOData[1]?.difference || '');
            row++;

            spacerRows.push(row);
            [2, 3, 4].forEach(col => cvdWorksheet.getCell(row, col).value = '');
            row++;

            cvdWorksheet.mergeCells(row, 2, row, 4);
            cvdWorksheet.getCell(row, 2).value = reportConstants.weightBalance;
            cvdWorksheet.getCell(row, 2).font = { bold: true, size: 12 };
            row++;

            cvdWorksheet.getCell(row, 2).value = reportConstants.gasWeightLoaded;
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 3), this.formatCvdMaterialBalanceValue(weightData[0]?.gasWeightLoaded, 2));
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 4), weightData[1]?.gasWeightLoaded || '');
            row++;

            cvdWorksheet.getCell(row, 2).value = reportConstants.weightCollected;
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 3), this.formatCvdMaterialBalanceValue(weightData[0]?.weightCollected, 2));
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 4), weightData[1]?.weightCollected || '');
            row++;

            cvdWorksheet.getCell(row, 2).value = reportConstants.dlDifference;
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 3), this.formatCvdMaterialBalanceValue(weightData[0]?.difference, 2));
            setNumericOrTextCellValue(cvdWorksheet.getCell(row, 4), weightData[1]?.difference || '');

            for (let r = 5; r <= row; r++) {
                for (let c = 2; c <= 4; c++) {
                    cvdWorksheet.getCell(r, c).border = borderStyle as Partial<Borders>;
                    cvdWorksheet.getCell(r, c).alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
                }
            }

            [2, 3, 4].forEach(col => {
                cvdWorksheet.getCell(5, col).border = {
                    bottom: { style: reportConstants.thin }
                };
            });

            // spacer rows keep only the outer side borders so the box stays continuous
            spacerRows.forEach(r => {
                [2, 3, 4].forEach(col => {
                    cvdWorksheet.getCell(r, col).border = {};
                });
                cvdWorksheet.getCell(r, 2).border = {
                    left: { style: reportConstants.thin }
                };
                cvdWorksheet.getCell(r, 4).border = {
                    right: { style: reportConstants.thin }
                };
            });

            cvdWorksheet.eachRow(row => row.eachCell(cell => {
                cell.font = { ...cell.font, name: reportConstants.eMprintCondensed };
            }));

        } catch (error) {
            console.error(reportConstantsMessage.errorGeneratingMaterialBalanceSheet, error);
        }
    }

    public AddOuterBorder(
        worksheet: Worksheet,
        startRowNo: number,
        endRowNo: number,
        startColumnNo: number,
        endColumnNo: number
    ) {
        for (let r = startRowNo; r <= endRowNo; r++) {
            for (let c = startColumnNo; c <= endColumnNo; c++) {
                const cell = worksheet.getCell(r, c);
                const currentTop = cell.border && cell.border.top ? cell.border.top : undefined;
                const currentBottom = cell.border && cell.border.bottom ? cell.border.bottom : undefined;
                const currentLeft = cell.border && cell.border.left ? cell.border.left : undefined;
                const currentRight = cell.border && cell.border.right ? cell.border.right : undefined;

                cell.border = {
                    top: r === startRowNo ? { style: reportConstants.thin } : currentTop,
                    bottom: r === endRowNo ? { style: reportConstants.thin } : currentBottom,
                    left: c === startColumnNo ? { style: reportConstants.thin } : currentLeft,
                    right: c === endColumnNo ? { style: reportConstants.thin } : currentRight,
                };
            }
        }
    }

    GenerateTableWithBorders(
        worksheet: Worksheet,
        tableHeader: string[][],
        tableBody: (string | number)[][],
        tableStartRow: number,
        tableStartColumn: number,
        borderStyle: { top: { style: string; }; left: { style: string; }; bottom: { style: string; }; right: { style: string; } }
    ) {
        tableHeader.forEach((headerRow, idx) => {
            headerRow.forEach((header, colIdx) => {
                const cell = worksheet.getCell(tableStartRow + idx, tableStartColumn + colIdx);
                cell.value = header;
                cell.font = { name: reportConstants.eMprintCondensed, size: 11, bold: true };
                cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle, wrapText: true };
                cell.border = {
                    top: idx === 0 ? { style: reportConstants.medium } : borderStyle.top,
                    bottom: idx === tableHeader.length - 1 ? { style: reportConstants.double } : borderStyle.bottom,
                    left: colIdx === 0 ? { style: reportConstants.medium } : borderStyle.left,
                    right: colIdx === headerRow.length - 1 ? { style: reportConstants.medium } : borderStyle.right,
                } as Partial<Borders>;

                const headerValue = header.split('\n');
                if (headerValue && headerValue.length > 0 && headerValue[0].length > 0) {
                    worksheet.getColumn(tableStartColumn + colIdx).width = headerValue[0].length + 2;
                } else {
                    worksheet.getColumn(tableStartColumn + colIdx).width = 8;
                }
                worksheet.getRow(tableStartRow + idx).height = 80;
            });
        });

        tableBody.forEach((row, rowIdx) => {
            row.forEach((cellValue, colIdx) => {
                const cell = worksheet.getCell(tableStartRow + tableHeader.length + rowIdx, tableStartColumn + colIdx);
                setNumericOrTextCellValue(cell, cellValue);
                cell.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
                cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                cell.border = {
                    bottom: rowIdx === tableBody.length - 1 ? { style: reportConstants.medium } : borderStyle.bottom,
                    left: colIdx === 0 ? { style: reportConstants.medium } : borderStyle.left,
                    right: colIdx === row.length - 1 ? { style: reportConstants.medium } : borderStyle.right,
                } as Partial<Borders>;
            });
        });
    }

    AddComment(worksheet: Worksheet, column: string, rowNo: number, value: string, fontSize = 11) {
        const textCommentsCell = worksheet.getCell(column + rowNo);
        textCommentsCell.value = value;
        textCommentsCell.font = { name: reportConstants.eMprintCondensed, size: fontSize, bold: false };
        textCommentsCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
        worksheet.getRow(rowNo).height = 15;
    }
}